import { authResult } from '@xdocs/schemas';
import type { AuthResult } from '@xdocs/schemas';
import { resolveWsHttpUrl } from '../lib/env.js';

/**
 * Client-side transport for the refresh-cookie lifecycle. All three calls
 * ride the SAME `/ws` route as plain (non-upgrade) HTTP requests — see
 * `apps/server/src/plugins/websocket.ts`'s doc comment: a WS `res` frame
 * can't carry `Set-Cookie`, and CODE_GUIDELINES §5.8's closed REST-route
 * list has no room for new `/auth/*` routes, so the cookie-setting/-reading
 * round trips are query-param branches on `/ws`, answered by a `preHandler`
 * hook before the WebSocket upgrade runs.
 */

/**
 * After `auth.signup`/`auth.login`/magic-link/password-reset succeed over
 * WS, exchange the freshly-issued access token for the httpOnly refresh
 * cookie in ONE plain fetch (`?bootstrap=<accessToken>`) — without this, a
 * hard reload has nothing to silently refresh from. Best-effort: a network
 * hiccup here must never fail an otherwise-successful login, it only means
 * this one reload cycle won't resume (the user stays logged in for the
 * current tab either way — the access token is already in memory).
 */
export async function mintSessionCookie(accessToken: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  try {
    const url = `${resolveWsHttpUrl()}?bootstrap=${encodeURIComponent(accessToken)}`;
    await fetchImpl(url, { credentials: 'include' });
  } catch {
    // best-effort — see doc comment above
  }
}

/**
 * Boot-time silent refresh (E20 resume): exchanges the httpOnly refresh
 * cookie (if any, and if still valid) for a fresh access token via
 * `?refresh=1`, which also rotates the cookie (one-time-use). Returns
 * `null` for every failure mode (no cookie, expired, revoked, malformed
 * response, network error) — the caller's contract is "stay
 * unauthenticated", never surface an error for what is, from the user's
 * perspective, simply "not logged in yet".
 */
export async function silentRefresh(fetchImpl: typeof fetch = fetch): Promise<AuthResult | null> {
  try {
    const url = `${resolveWsHttpUrl()}?refresh=1`;
    const response = await fetchImpl(url, { credentials: 'include' });
    if (!response.ok) return null;
    const parsed = authResult.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Best-effort refresh-cookie clear on logout (`?logout=1`). By the time this
 * runs, the DB-side session is already revoked (`auth.logout`); this just
 * spares the browser a stale cookie sitting around until the next silent-
 * refresh attempt would clear it lazily anyway.
 */
export async function clearSessionCookie(fetchImpl: typeof fetch = fetch): Promise<void> {
  try {
    const url = `${resolveWsHttpUrl()}?logout=1`;
    await fetchImpl(url, { credentials: 'include' });
  } catch {
    // best-effort — see doc comment above
  }
}
